<template>
  <div v-if="userGroupList && userGroupList.length > 0" class="avocash-groups-section">
    <div class="section-header">
      <h3 class="section-title">{{ $t('groups.title') }}</h3>
      <span class="section-count">{{ $t('groups.subtitle', { count: userGroupList.length }) }}</span>
    </div>

    <div class="groups-grid">
      <f7-card
        v-for="group in userGroupList"
        :key="group.id"
        class="group-card"
        :padding="false"
        @click="openGroup(group)"
      >
        <!-- Banner -->
        <div class="group-banner" :class="{ 'group-banner--empty': !bannerUrl(group) }">
          <img v-if="bannerUrl(group)" :src="bannerUrl(group)" :alt="group.name" class="banner-img" />
          <span v-else class="material-symbols-outlined banner-placeholder">diversity_3</span>
          <span
            v-if="statusLabel(group)"
            class="status-pill"
            :class="'status-' + (group.status || '').toLowerCase()"
          >
            {{ statusLabel(group) }}
          </span>
        </div>

        <!-- Identity -->
        <div class="group-body">
          <div class="group-head">
            <img v-if="logoUrl(group)" :src="logoUrl(group)" :alt="group.name" class="group-logo" />
            <div v-else class="group-logo group-logo--fallback">{{ initials(group.name) }}</div>
            <div class="group-head-text">
              <div class="group-name">{{ group.name }}</div>
              <div class="group-subline">
                <span v-if="typeLabel(group)" class="type-chip">{{ typeLabel(group) }}</span>
                <span v-if="group.city" class="city">{{ group.city }}</span>
              </div>
            </div>
          </div>

          <p v-if="group.description" class="group-desc" v-html="group.description"></p>

          <div class="group-meta">
            <span v-if="group.memberCount != null" class="meta-item">
              <span class="material-symbols-outlined">group</span>
              {{ $t('groups.members', { count: group.memberCount }) }}
            </span>
            <span v-if="group.tag" class="meta-item meta-tag">{{ group.tag }}</span>
          </div>

          <div v-if="group.status === 'DRAFT'" class="draft-hint">
            <span class="material-symbols-outlined">info</span>
            {{ $t('groups.draftHint') }}
          </div>
        </div>

        <!-- Balance footer -->
        <f7-card-footer v-if="hasBalance(group)" class="group-footer">
          <span class="balance-label">{{ $t('groups.balance') }}</span>
          <span class="balance-val">{{ $filtersToCurrency.toCurrency(group.balance, null, group.currencyCode) }}</span>
        </f7-card-footer>
      </f7-card>
    </div>
  </div>
</template>

<style scoped>
.avocash-groups-section {
  margin: 16px 0 20px;
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 16px 12px;
}
.section-title {
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--avocash-on-surface, #161d18);
  margin: 0;
}
.section-count {
  font-family: var(--avocash-font-body, 'Work Sans', sans-serif);
  font-size: 11px;
  font-weight: 600;
  color: var(--avocash-outline, #6f7a71);
  background: var(--avocash-surface-container, #e8f0e7);
  padding: 3px 9px;
  border-radius: 9999px;
}

/* ── Responsive grid: 1 card per row on small screens, 2 otherwise ── */
.groups-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (min-width: 640px) {
  .groups-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ── Card ── */
.group-card {
  margin: 0;
  width: auto;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s;
}
.group-card:active {
  transform: scale(0.98);
}

/* ── Banner ── */
.group-banner {
  position: relative;
  height: 104px;
  background: linear-gradient(135deg, #1a6b42 0%, #00512e 100%);
  overflow: hidden;
}
.banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.banner-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: rgba(255, 255, 255, 0.35);
  font-variation-settings: 'FILL' 1;
}
.status-pill {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 3px 8px;
  border-radius: 9999px;
  backdrop-filter: blur(4px);
}
.status-pill.status-draft {
  background: rgba(142, 142, 147, 0.85);
  color: #fff;
}
.status-pill.status-archived {
  background: rgba(142, 142, 147, 0.85);
  color: #fff;
}
.status-pill.status-suspended {
  background: rgba(119, 46, 55, 0.9);
  color: #fff;
}

/* ── Body ── */
.group-body {
  padding: 0 14px 12px;
}
.group-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: -22px;
}
.group-logo {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  object-fit: cover;
  flex-shrink: 0;
  border: 2.5px solid #fff;
  background: var(--f7-card-bg-color, #fff);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.group-logo--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--f7-theme-color, #1a6b42);
  color: #fff;
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 14px;
  font-weight: 700;
}
.group-head-text {
  min-width: 0;
  padding-top: 26px;
  flex: 1;
}
.group-name {
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 15px;
  font-weight: 700;
  color: var(--avocash-on-surface, #161d18);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.group-subline {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  min-width: 0;
}
.type-chip {
  font-size: 10px;
  font-weight: 700;
  color: var(--f7-theme-color, #1a6b42);
  background: color-mix(in srgb, var(--f7-theme-color, #1a6b42) 10%, transparent);
  padding: 2px 8px;
  border-radius: 9999px;
  white-space: nowrap;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.city {
  font-size: 11px;
  color: var(--avocash-outline, #6f7a71);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.group-desc {
  font-size: 12px;
  line-height: 1.4;
  color: var(--avocash-outline, #6f7a71);
  margin: 8px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Meta row ── */
.group-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--avocash-outline, #6f7a71);
}
.meta-item .material-symbols-outlined {
  font-size: 15px;
  font-variation-settings: 'FILL' 1;
  color: var(--f7-theme-color, #1a6b42);
}
.meta-tag {
  background: var(--avocash-surface-container, #e8f0e7);
  padding: 2px 8px;
  border-radius: 9999px;
  max-width: 50%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Draft hint ── */
.draft-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #a08520;
}
.draft-hint .material-symbols-outlined {
  font-size: 14px;
  font-variation-settings: 'FILL' 1;
}

/* ── Balance footer ── */
.group-footer {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-top: 1px solid rgba(111, 122, 113, 0.1);
}
.balance-label {
  font-size: 10px;
  color: var(--avocash-outline, #6f7a71);
}
.balance-val {
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 15px;
  font-weight: 700;
  color: var(--f7-theme-color, #1a6b42);
}
</style>

<script setup>
import { computed } from "vue";
import { f7 } from "framework7-vue";
import { useAccountStore } from "../js/pinia-stores/account.js";
import i18n from "../js/i18n.js";

const { t } = i18n.global;
const accountStore = useAccountStore();

const userGroupList = computed(() => accountStore.userGroupList);

function logoUrl(group) {
  return group?.logoUrl || group?.logo || "";
}

function bannerUrl(group) {
  return group?.bannerUrl || group?.banner || "";
}

function initials(name) {
  if (!name) return "G";
  return String(name).trim().slice(0, 2).toUpperCase();
}

function truncate(text, length = 90) {
  const str = String(text || "");
  return str.length > length ? str.slice(0, length).trim() + "…" : str;
}

// New GroupDTO categories are translated; legacy types (WALL, ASSO…) fall
// back to the raw value.
function typeLabel(group) {
  if (group?.category) {
    const key = `groups.category.${group.category}`;
    const label = t(key);
    if (label !== key) return label;
  }
  return group?.type || "";
}

// Only surface statuses that need attention (DRAFT, ARCHIVED, SUSPENDED);
// ACTIVE is the norm and would add noise on every card.
function statusLabel(group) {
  if (!group?.status || group.status === "ACTIVE") return "";
  const key = `groups.status.${group.status}`;
  const label = t(key);
  return label !== key ? label : group.status;
}

function hasBalance(group) {
  return group?.balance !== undefined && group?.balance !== null && group.balance !== "";
}

function openGroup(group) {
  f7.views.main.router.navigate(`/groups/${group.id}`);
}
</script>